<template>
  <div
    class="fixed top-0 left-0 w-screen h-screen z-[100001] opacity-0 pointer-events-none transition-all duration-500"
    :class="{ 'opacity-100 pointer-events-auto': state.isVisible }">
    <div
      class="absolute top-0 left-0 w-full h-full z-10 opacity-70 bg-black backdrop-blur-lg"
      @click="state.isVisible = false"></div>
    <div
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-0 w-full max-w-2xl p-5 h-full z-20 flex items-center transition-all duration-500"
      :class="{ 'scale-100': state.isVisible }">
      <div class="relative w-full h-fit max-h-[90vh] bg-white overflow-y-auto rounded-2xl">
        <div class="w-full p-6 flex flex-col gap-5">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold">Search & Impersonate User</h2>
            <button
              class="w-8 h-8 flex items-center justify-center cursor-pointer text-gray hover:text-black tr-all text-2xl"
              @click="state.isVisible = false">
              &times;
            </button>
          </div>

          <div class="flex gap-2">
            <input
              v-model="state.searchQuery"
              type="text"
              placeholder="Search by email, first or last name..."
              class="flex-1 h-10 border border-gray rounded px-3 text-sm"
              @keyup.enter="search" />
            <button
              class="h-10 px-5 flex items-center justify-center cursor-pointer rounded bg-red hover:bg-dark-red text-white tr-all text-sm"
              :disabled="state.isSearching"
              @click="search">
              {{ state.isSearching ? 'Searching...' : 'Search' }}
            </button>
          </div>

          <div
            v-if="state.searchResults.length > 0"
            class="w-full">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray">
                  <th class="text-left py-2 px-1">Email</th>
                  <th class="text-left py-2 px-1">Name</th>
                  <th class="text-center py-2 px-1">Active</th>
                  <th class="text-center py-2 px-1">Verified</th>
                  <th class="text-right py-2 px-1"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="user in state.searchResults"
                  :key="user.id"
                  class="border-b border-gray/30 hover:bg-gray/10">
                  <td class="py-2 px-1">{{ user.email }}</td>
                  <td class="py-2 px-1">{{ user.first_name }} {{ user.last_name }}</td>
                  <td class="text-center py-2 px-1">{{ user.is_active ? 'Yes' : 'No' }}</td>
                  <td class="text-center py-2 px-1">{{ user.is_verified ? 'Yes' : 'No' }}</td>
                  <td class="text-right py-2 px-1">
                    <button
                      class="h-8 px-3 cursor-pointer rounded bg-red hover:bg-dark-red text-white tr-all text-xs"
                      @click="impersonate(user.id)">
                      Impersonate
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-else-if="state.hasSearched && !state.isSearching"
            class="text-sm text-gray">
            No users found.
          </div>

          <div class="w-full border-t border-gray/30"></div>

          <div class="flex flex-col gap-3">
            <h3 class="text-base font-semibold">Create New User</h3>
            <div class="flex flex-wrap gap-2">
              <input
                v-model="state.createForm.email"
                type="email"
                placeholder="Email (required)"
                class="flex-1 min-w-[200px] h-10 border border-gray rounded px-3 text-sm" />
              <input
                v-model="state.createForm.first_name"
                type="text"
                placeholder="First name"
                class="w-36 h-10 border border-gray rounded px-3 text-sm" />
              <input
                v-model="state.createForm.last_name"
                type="text"
                placeholder="Last name"
                class="w-36 h-10 border border-gray rounded px-3 text-sm" />
            </div>

            <div
              v-if="state.createError"
              class="text-red text-sm">
              {{ state.createError }}
            </div>

            <div class="flex gap-2">
              <button
                class="h-9 px-4 flex items-center justify-center cursor-pointer rounded bg-red hover:bg-dark-red text-white tr-all text-sm"
                :disabled="state.isCreating"
                @click="createUser(false)">
                {{ state.isCreating ? 'Creating...' : 'Create User' }}
              </button>
              <button
                class="h-9 px-4 flex items-center justify-center cursor-pointer rounded bg-red hover:bg-dark-red text-white tr-all text-sm"
                :disabled="state.isCreating"
                @click="createUser(true)">
                Create & Impersonate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, onMounted, onUnmounted } from 'vue';
import { AdminApiHelper } from '@/helpers/global/baseAPI.helper';

const state = reactive({
  isVisible: false,
  searchQuery: '',
  searchResults: [] as any[],
  hasSearched: false,
  isSearching: false,
  isCreating: false,
  createError: '',
  createForm: {
    email: '',
    first_name: '',
    last_name: '',
  },
});

const search = async () => {
  const q = state.searchQuery.trim();
  if (q.length < 2) return;

  state.isSearching = true;
  state.hasSearched = true;

  try {
    const data = await AdminApiHelper.get(`/impersonate-users/search/?q=${encodeURIComponent(q)}`);

    if (Array.isArray(data)) {
      state.searchResults = data;
    }
  } finally {
    state.isSearching = false;
  }
};

const createUser = async (andImpersonate: boolean) => {
  state.createError = '';

  if (!state.createForm.email) {
    state.createError = 'Email is required.';
    return;
  }

  state.isCreating = true;

  try {
    const response = await AdminApiHelper.post('/impersonate-users/create/', {
      email: state.createForm.email,
      first_name: state.createForm.first_name,
      last_name: state.createForm.last_name,
    });

    if (response.status === 201) {
      state.createForm.email = '';
      state.createForm.first_name = '';
      state.createForm.last_name = '';

      if (andImpersonate) {
        impersonate(response.data.id);
      } else {
        if (state.searchQuery.trim().length >= 2) {
          await search();
        }
      }
    } else if (response.data?.email) {
      state.createError = Array.isArray(response.data.email) ? response.data.email[0] : response.data.email;
    }
  } finally {
    state.isCreating = false;
  }
};

const impersonate = (userId: number) => {
  const form = document.createElement('form');
   if (typeof HIJACK_URL === 'undefined' ){
     return;
   }
  form.method = 'POST';
  form.action = `${HIJACK_URL}/acquire`;
  form.style.display = 'none';

  const csrfInput = document.createElement('input');
  csrfInput.type = 'hidden';
  csrfInput.name = 'csrfmiddlewaretoken';
  csrfInput.value = CSRF_TOKEN;
  form.appendChild(csrfInput);

  const userInput = document.createElement('input');
  userInput.type = 'hidden';
  userInput.name = 'user_pk';
  userInput.value = String(userId);
  form.appendChild(userInput);

  document.body.appendChild(form);
  form.submit();
};

const onOpenEvent = () => {
  state.isVisible = true;
};

onMounted(() => {
  window.addEventListener('open-search-impersonate', onOpenEvent);
});

onUnmounted(() => {
  window.removeEventListener('open-search-impersonate', onOpenEvent);
});
</script>
